import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  createAdminClientTab,
  deleteAdminClientTab,
  listAdminClientTabs,
  listAvailableKeysForAdmin,
  reorderAdminClientTabs,
  updateAdminClientTab,
} from 'api/clientTabsTenant';
import {
  AVAILABLE_ICONS,
  getTabDefaults,
  resolveIcon,
} from 'providers/ClientTabs/clientTabsRegistry';
import SortableTabsTable from 'providers/ClientTabs/SortableTabsTable';
import useTenantFlags from 'hooks/useTenantFlags';
import { useEffect, useMemo, useState } from 'react';

// Drawer que usa el tenant (ejecutivo) para configurar qué tabs ven sus
// administradores. La lista de keys seleccionables viene del backend
// (available-keys: keys habilitadas para ejec en este tenant). Es la
// mitad tenant de la cascada supertenant → tenant → admin.
export default function AdministradorTabsConfig({ open, onClose }) {
  const { readOnly } = useTenantFlags();
  const [rows, setRows] = useState([]);
  const [availableKeys, setAvailableKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [list, keys] = await Promise.all([listAdminClientTabs(), listAvailableKeysForAdmin()]);
      setRows(Array.isArray(list) ? list : []);
      setAvailableKeys(Array.isArray(keys) ? keys : []);
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudieron cargar los tabs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
    if (!open) {
      setRows([]);
      setAvailableKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const usedKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);
  const addableKeys = useMemo(
    () => availableKeys.filter((k) => !usedKeys.has(k)),
    [availableKeys, usedKeys]
  );

  const commitField = async (row, field, value) => {
    if (row[field] === value) return;
    setSavingId(row.id);
    try {
      const updated = await updateAdminClientTab(row.id, { [field]: value });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo actualizar');
      await refresh();
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (row) => {
    try {
      await deleteAdminClientTab(row.id);
      message.success(`Tab "${row.key}" eliminado del administrador.`);
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo eliminar');
    }
  };

  const onAdd = async (values) => {
    try {
      await createAdminClientTab({
        key: values.key,
        label: values.label,
        icon: values.icon || null,
        order: values.order ?? rows.length + 1,
      });
      message.success('Tab agregado al administrador');
      setAddOpen(false);
      await refresh();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'CASCADE_VIOLATION') {
        message.error(
          'Ese tab no está habilitado por el supertenant para el ejecutivo de tu tenant. Pedile que lo habilite primero.'
        );
      } else {
        message.error(err?.response?.data?.mensaje || 'No se pudo crear el tab');
      }
    }
  };

  const onReorder = async (reorderedRows) => {
    const withOrder = reorderedRows.map((row, index) => ({ ...row, order: index + 1 }));
    setRows(withOrder);
    try {
      await reorderAdminClientTabs(withOrder.map(({ id, order }) => ({ id, order })));
    } catch (err) {
      message.error(err?.response?.data?.mensaje || 'No se pudo reordenar');
      await refresh();
    }
  };

  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [rows]
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={820}
        title="Tabs del administrador"
        destroyOnHidden
        extra={
          !readOnly && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddOpen(true)}
              disabled={loading || addableKeys.length === 0}
            >
              Agregar tab
            </Button>
          )
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Aplica a todos tus administradores"
          description="Los cambios se reflejan para todos los administradores del tenant al recargar. Arrastra la manija para reordenar. Solo podés habilitar tabs que el supertenant tenga activos para tu ejecutivo."
        />
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <SortableTabsTable
            rows={orderedRows}
            savingId={savingId}
            onEditField={readOnly ? undefined : commitField}
            onDelete={readOnly ? undefined : onDelete}
            onReorder={readOnly ? undefined : onReorder}
            readOnly={readOnly}
          />
        )}
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
          Keys disponibles para agregar (habilitadas para ejec por el supertenant y no usadas aún):{' '}
          {addableKeys.length ? <code>{addableKeys.join(', ')}</code> : <em>ninguna</em>}
        </Typography.Paragraph>
      </Drawer>

      <AddTabModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        availableKeys={addableKeys}
        onSubmit={onAdd}
      />
    </>
  );
}

function AddTabModal({ open, onClose, availableKeys, onSubmit }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const handleKeyChange = (key) => {
    const defaults = getTabDefaults(key);
    if (defaults) {
      form.setFieldsValue({
        label: defaults.label,
        icon: defaults.icon,
      });
    }
  };

  return (
    <Modal
      open={open}
      title="Agregar tab al administrador"
      okText="Agregar"
      cancelText="Cancelar"
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form layout="vertical" form={form} onFinish={onSubmit}>
        <Form.Item
          label="Key"
          name="key"
          rules={[{ required: true, message: 'Seleccioná un key' }]}
        >
          <Select
            placeholder="Elegí un key de la lista"
            onChange={handleKeyChange}
            options={availableKeys.map((k) => ({ value: k, label: k }))}
            showSearch
          />
        </Form.Item>
        <Form.Item label="Label" name="label" rules={[{ required: true, message: 'Requerido' }]}>
          <Input placeholder="Texto visible en la UI" />
        </Form.Item>
        <Form.Item label="Ícono" name="icon">
          <Select
            allowClear
            placeholder="Ícono opcional"
            options={AVAILABLE_ICONS.map((name) => ({
              value: name,
              label: (
                <Space>
                  {resolveIcon(name)}
                  <span>{name}</span>
                </Space>
              ),
            }))}
          />
        </Form.Item>
        <Form.Item label="Orden" name="order">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
